import { useLayoutEffect, useState, type RefObject } from 'react'
import { baseTheme } from '../../styles/baseTheme'

const ENTER_PX = parseInt(baseTheme.breakpoints.layoutMobile, 10) || 720
/** Hysteresis so resizing near the breakpoint doesn't flicker. */
const EXIT_PX = ENTER_PX + 96
/** Treat the desktop toolbar as overflowing when the spacer is this tight. */
const MIN_SPACER_PX = 24

/**
 * Switches ListView chrome (toolbar + footer) to the alternate stacked layout
 * when the container is narrow or the desktop toolbar would wrap / collide.
 */
export function useNarrowChrome(
  containerRef: RefObject<HTMLElement | null>,
): boolean {
  const [narrow, setNarrow] = useState(false)

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return

    const measure = () => {
      const width = el.clientWidth
      setNarrow((prev) => {
        if (prev) {
          // Stay compact until clearly wide enough again.
          return width < EXIT_PX
        }

        if (width < ENTER_PX) return true

        const toolbar = el.querySelector('[data-listview-toolbar]')
        if (!(toolbar instanceof HTMLElement)) return false

        // Desktop toolbar uses flex-wrap — detect a second row of chips/controls.
        const kids = Array.from(toolbar.children).filter(
          (c): c is HTMLElement =>
            c instanceof HTMLElement &&
            c.dataset.toolbarSpacer == null &&
            c.dataset.actionsMeasure == null &&
            getComputedStyle(c).display !== 'none',
        )
        if (kids.length > 1) {
          const top = kids[0].offsetTop
          if (kids.some((c) => Math.abs(c.offsetTop - top) > 6)) return true
        }

        const spacer = toolbar.querySelector('[data-toolbar-spacer]')
        if (
          spacer instanceof HTMLElement &&
          spacer.offsetWidth > 0 &&
          spacer.offsetWidth < MIN_SPACER_PX
        ) {
          return true
        }

        return false
      })
    }

    measure()
    const ro = new ResizeObserver(() => measure())
    ro.observe(el)
    window.addEventListener('resize', measure)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [containerRef])

  return narrow
}
